
import { supabase } from '@/integrations/supabase/client';
import { TrekPackage } from '@/lib/types/trek-types';

/**
 * Get packages for a specific trek
 */
export async function getPackagesByTrekId(trekId: string): Promise<TrekPackage[]> {
  try {
    const { data, error } = await supabase
      .from('trek_packages')
      .select('*')
      .eq('trek_id', trekId)
      .eq('is_active', true);
      
    if (error) throw error;
    
    if (data && data.length > 0) {
      return data.map(pkg => ({
        id: pkg.id,
        trekId: pkg.trek_id,
        companyId: pkg.company_id,
        name: pkg.name || `${pkg.trek_id} Package`, // Default name if not provided
        description: pkg.description || '', // Default empty description
        price: pkg.price,
        duration: pkg.duration,
        maxGroupSize: pkg.max_group_size,
        startDates: pkg.start_dates,
        includedMeals: pkg.included_meals,
        transport: pkg.transport,
        equipmentIncluded: pkg.equipment_included,
        experiencedGuides: pkg.experienced_guides,
        accommodationType: pkg.accommodation_type,
        cancellationPolicy: pkg.cancellation_policy,
        highlightFeatures: pkg.highlight_features,
        booking_link: pkg.booking_link
      }));
    }
    
    return [];
  } catch (error) {
    console.warn(`Error fetching packages for trek ${trekId} from Supabase:`, error);
    return [];
  }
}

/**
 * Get packages for a specific company
 */
export async function getPackagesByCompanyId(companyId: string): Promise<TrekPackage[]> {
  try {
    const { data, error } = await supabase
      .from('trek_packages')
      .select('*')
      .eq('company_id', companyId)
      .eq('is_active', true);
      
    if (error) throw error;
    
    if (data && data.length > 0) {
      return data.map(pkg => ({
        id: pkg.id,
        trekId: pkg.trek_id,
        companyId: pkg.company_id,
        name: pkg.name || `${pkg.trek_id} Package`, // Default name if not provided
        description: pkg.description || '', // Default empty description
        price: pkg.price,
        duration: pkg.duration,
        maxGroupSize: pkg.max_group_size,
        startDates: pkg.start_dates,
        includedMeals: pkg.included_meals,
        transport: pkg.transport,
        equipmentIncluded: pkg.equipment_included,
        experiencedGuides: pkg.experienced_guides,
        accommodationType: pkg.accommodation_type,
        cancellationPolicy: pkg.cancellation_policy,
        highlightFeatures: pkg.highlight_features,
        booking_link: pkg.booking_link
      }));
    }
    
    return [];
  } catch (error) {
    console.warn(`Error fetching packages for company ${companyId} from Supabase:`, error);
    return [];
  }
}

/**
 * Get a specific package by ID
 */
export async function getPackageById(packageId: string): Promise<TrekPackage | null> {
  try {
    // Use maybeSingle() instead of single() to handle the case when no data is found
    const { data, error } = await supabase
      .from('trek_packages')
      .select('*')
      .eq('id', packageId)
      .maybeSingle();
      
    if (error) throw error;
    
    if (!data) {
      console.warn(`Package with ID ${packageId} not found in database`);
      return null;
    }
    
    return {
      id: data.id,
      trekId: data.trek_id,
      companyId: data.company_id,
      name: data.name || `${data.trek_id} Package`, // Default name if not provided
      description: data.description || '', // Default empty description
      price: data.price,
      duration: data.duration,
      maxGroupSize: data.max_group_size,
      startDates: data.start_dates,
      includedMeals: data.included_meals,
      transport: data.transport,
      equipmentIncluded: data.equipment_included,
      experiencedGuides: data.experienced_guides,
      accommodationType: data.accommodation_type,
      cancellationPolicy: data.cancellation_policy,
      highlightFeatures: data.highlight_features,
      booking_link: data.booking_link
    };
  } catch (error) {
    console.warn(`Error fetching package ${packageId} from Supabase:`, error);
    return null;
  }
}
